<template>
  <div :aria-label="avatarLabel"
       :class="avatarToneClass"
       :role="shouldShowImage || !hasDisplayName ? null : 'img'"
       class="interactive-social-wall__avatar">
    <img v-if="shouldShowImage" :alt="displayName" :src="normalizedPhoto" @error="handleImageError"/>
    <span v-else-if="hasDisplayName" aria-hidden="true">{{ fallbackInitials }}</span>
  </div>
</template>

<script>
const TURKISH_LOCALE = "tr-TR";
const AVATAR_TONES = [
  "navy",
  "azure",
  "teal",
  "forest",
  "olive",
  "bronze",
  "rust",
  "wine",
  "plum",
  "slate",
];
const AVATAR_HASH_MODULUS = 2147483647;

function avatarToneFromName(name) {
  const normalizedName = String(name || "").toLocaleUpperCase(TURKISH_LOCALE);
  const hash = Array.from(normalizedName).reduce(
      (currentHash, character) =>
        (currentHash * 31 + character.charCodeAt(0)) % AVATAR_HASH_MODULUS,
      0
  );

  return AVATAR_TONES[hash % AVATAR_TONES.length];
}

export default {
  name: "SocialWallAvatar",
  props: {
    name: {
      type: String,
      default: "",
    },
    photo: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      hasImageError: false,
    };
  },
  computed: {
    displayName() {
      return typeof this.name === "string" ? this.name.trim() : "";
    },
    hasDisplayName() {
      return Boolean(this.displayName);
    },
    normalizedPhoto() {
      return typeof this.photo === "string" ? this.photo.trim() : "";
    },
    shouldShowImage() {
      return Boolean(this.normalizedPhoto) && !this.hasImageError;
    },
    avatarTone() {
      return avatarToneFromName(this.displayName);
    },
    avatarToneClass() {
      if (!this.hasDisplayName && !this.shouldShowImage) {
        return "interactive-social-wall__avatar--pending";
      }
      return `interactive-social-wall__avatar--${this.avatarTone}`;
    },
    avatarLabel() {
      if (this.shouldShowImage || !this.hasDisplayName) return null;
      return `${this.displayName} profil resmi`;
    },
    fallbackInitials() {
      const nameParts = this.displayName.split(/\s+/).filter(Boolean);
      if (!nameParts.length) return "";

      const firstNameCharacters = Array.from(nameParts[0]);
      const initials =
          nameParts.length > 1
              ? `${firstNameCharacters[0]}${
                  Array.from(nameParts[nameParts.length - 1])[0]
              }`
              : `${firstNameCharacters[0]}${
                  firstNameCharacters[1] || firstNameCharacters[0]
              }`;

      return initials.toLocaleUpperCase(TURKISH_LOCALE);
    },
  },
  watch: {
    normalizedPhoto() {
      this.hasImageError = false;
    },
  },
  methods: {
    handleImageError(event) {
      this.hasImageError = true;
      this.$emit("error", event);
    },
  },
};
</script>

<style scoped>
.interactive-social-wall__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  font-weight: 700;
  border-radius: 50%;
  color: #fff;
}

:where(.interactive-social-wall__avatar--pending) {
  background: #e8edf3;
}

:where(.interactive-social-wall__avatar--navy) {
  --iconColor: #fff;
  background: linear-gradient(145deg, #3a4a78 0%, #243056 100%);
}

:where(.interactive-social-wall__avatar--azure) {
  --iconColor: #fff;
  background: linear-gradient(145deg, #1a73c4 0%, #0f5fa8 100%);
}

:where(.interactive-social-wall__avatar--teal) {
  --iconColor: #fff;
  background: linear-gradient(145deg, #14908a 0%, #0b6e6a 100%);
}

:where(.interactive-social-wall__avatar--forest) {
  --iconColor: #fff;
  background: linear-gradient(145deg, #2a8a4c 0%, #1a6b38 100%);
}

:where(.interactive-social-wall__avatar--olive) {
  --iconColor: #fff;
  background: linear-gradient(145deg, #6b7c28 0%, #4a5c16 100%);
}

:where(.interactive-social-wall__avatar--bronze) {
  --iconColor: #fff;
  background: linear-gradient(145deg, #b07520 0%, #8a5a12 100%);
}

:where(.interactive-social-wall__avatar--rust) {
  --iconColor: #fff;
  background: linear-gradient(145deg, #c45a3c 0%, #a1432c 100%);
}

:where(.interactive-social-wall__avatar--wine) {
  --iconColor: #fff;
  background: linear-gradient(145deg, #9a3d5c 0%, #7a2d45 100%);
}

:where(.interactive-social-wall__avatar--plum) {
  --iconColor: #fff;
  background: linear-gradient(145deg, #7a42a8 0%, #5c2e82 100%);
}

:where(.interactive-social-wall__avatar--slate) {
  --iconColor: #fff;
  background: linear-gradient(145deg, #5a6578 0%, #3d4654 100%);
}

.interactive-social-wall__avatar img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.interactive-social-wall__avatar span {
  line-height: 1;
}
</style>
